import { useFrame } from "@react-three/fiber";
import { useEffect, useLayoutEffect, useMemo, useRef, type MutableRefObject } from "react";
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  type Group,
} from "three";
import type { Point2 } from "@games-monorepo/yak-trails-core";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";
import { TRAIL_CONTACT_Y, TRAIL_SURFACE_Y } from "./sceneLayers";
import type { PresentationPose } from "./motion";

function nextCapacity(required: number): number {
  let capacity = 8;
  while (capacity < required) capacity *= 2;
  return capacity;
}

function fillStaticIndices(capacity: number): Uint32Array {
  const indices = new Uint32Array(Math.max(0, capacity - 1) * 6);
  for (let index = 0; index < capacity - 1; index += 1) {
    const offset = index * 2;
    const indexOffset = index * 6;
    indices.set([offset, offset + 2, offset + 1, offset + 1, offset + 2, offset + 3], indexOffset);
  }
  return indices;
}

function fillUpwardNormals(capacity: number): Float32Array {
  const normals = new Float32Array(capacity * 6);
  for (let vertex = 0; vertex < capacity * 2; vertex += 1) normals[vertex * 3 + 1] = 1;
  return normals;
}

function writeRibbonPoints(
  positions: Float32Array,
  points: Point2[],
  halfWidth: number,
  surfaceY: number,
  startIndex: number,
): void {
  for (let index = startIndex; index < points.length; index += 1) {
    const point = points[index]!;
    const previous = points[Math.max(0, index - 1)]!;
    const next = points[Math.min(points.length - 1, index + 1)]!;
    const dx = next.x - previous.x;
    const dy = next.y - previous.y;
    const length = Math.hypot(dx, dy) || 1;
    const normalX = -dy / length * halfWidth;
    const normalY = dx / length * halfWidth;
    const offset = index * 6;
    positions.set([
      point.x + normalX, surfaceY, point.y + normalY,
      point.x - normalX, surfaceY, point.y - normalY,
    ], offset);
  }
}

function useDynamicRibbonGeometry(points: Point2[], halfWidth: number, surfaceY: number): BufferGeometry {
  const geometry = useMemo(() => new BufferGeometry(), []);
  const capacityRef = useRef(0);
  const pointCountRef = useRef(0);
  useLayoutEffect(() => {
    if (points.length < 2) {
      geometry.setDrawRange(0, 0);
      pointCountRef.current = points.length;
      return;
    }
    if (points.length > capacityRef.current) {
      capacityRef.current = nextCapacity(points.length);
      const positions = new Float32Array(capacityRef.current * 6);
      writeRibbonPoints(positions, points, halfWidth, surfaceY, 0);
      const positionAttribute = new BufferAttribute(positions, 3);
      positionAttribute.setUsage(DynamicDrawUsage);
      geometry.setAttribute("position", positionAttribute);
      geometry.setAttribute("normal", new BufferAttribute(fillUpwardNormals(capacityRef.current), 3));
      geometry.setIndex(new BufferAttribute(fillStaticIndices(capacityRef.current), 1));
    } else if (points.length !== pointCountRef.current) {
      const positionAttribute = geometry.getAttribute("position") as BufferAttribute;
      writeRibbonPoints(
        positionAttribute.array as Float32Array,
        points,
        halfWidth,
        surfaceY,
        points.length < pointCountRef.current ? 0 : Math.max(0, pointCountRef.current - 2),
      );
      positionAttribute.needsUpdate = true;
    }
    geometry.setDrawRange(0, (points.length - 1) * 6);
    pointCountRef.current = points.length;
  }, [geometry, halfWidth, points, surfaceY]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return geometry;
}

function LiveTrailTip({ from, endpoint, color, contactColor }: {
  from: Point2;
  endpoint: MutableRefObject<PresentationPose>;
  color: string;
  contactColor: Color;
}) {
  const group = useRef<Group>(null);
  useFrame(() => {
    if (!group.current) return;
    const deltaX = endpoint.current.x - from.x;
    const deltaY = endpoint.current.y - from.y;
    const length = Math.hypot(deltaX, deltaY);
    group.current.visible = length > YAK_TRAILS_CLIENT_TUNING.trail.tipVisibilityDistance;
    group.current.position.set((from.x + endpoint.current.x) / 2, 0, (from.y + endpoint.current.y) / 2);
    group.current.rotation.y = -Math.atan2(deltaY, deltaX);
    group.current.scale.x = length + YAK_TRAILS_CLIENT_TUNING.trail.tipOverlap;
  });
  return (
    <group ref={group}>
      <mesh
        position={[0, TRAIL_CONTACT_Y, 0]}
        scale={[
          1,
          YAK_TRAILS_CLIENT_TUNING.trail.tipContactDepth,
          YAK_TRAILS_CLIENT_TUNING.trail.tipContactWidth,
        ]}
        receiveShadow
        renderOrder={1}
      >
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color={contactColor} roughness={YAK_TRAILS_CLIENT_TUNING.trail.contactRoughness} />
      </mesh>
      <mesh
        position={[0, TRAIL_SURFACE_Y, 0]}
        scale={[
          1,
          YAK_TRAILS_CLIENT_TUNING.trail.tipSurfaceDepth,
          YAK_TRAILS_CLIENT_TUNING.trail.tipSurfaceWidth,
        ]}
        receiveShadow
        castShadow
        renderOrder={2}
      >
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial
          color={color}
          roughness={YAK_TRAILS_CLIENT_TUNING.trail.surfaceRoughness}
          polygonOffset
          polygonOffsetFactor={YAK_TRAILS_CLIENT_TUNING.trail.polygonOffsetFactor}
        />
      </mesh>
    </group>
  );
}

export function TrailRibbon({ points, color, liveEndpoint }: {
  points: Point2[];
  color: string;
  liveEndpoint?: MutableRefObject<PresentationPose>;
}) {
  const contactGeometry = useDynamicRibbonGeometry(
    points,
    YAK_TRAILS_CLIENT_TUNING.trail.contactHalfWidth,
    TRAIL_CONTACT_Y,
  );
  const surfaceGeometry = useDynamicRibbonGeometry(
    points,
    YAK_TRAILS_CLIENT_TUNING.trail.surfaceHalfWidth,
    TRAIL_SURFACE_Y,
  );
  const contactColor = useMemo(
    () => new Color(color).offsetHSL(0, -0.04, YAK_TRAILS_CLIENT_TUNING.trail.contactLightnessOffset),
    [color],
  );
  if (points.length < 2) return null;
  return (
    <group>
      <mesh geometry={contactGeometry} receiveShadow renderOrder={1} frustumCulled={false}>
        <meshStandardMaterial
          color={contactColor}
          roughness={YAK_TRAILS_CLIENT_TUNING.trail.contactRoughness}
          side={DoubleSide}
        />
      </mesh>
      <mesh geometry={surfaceGeometry} receiveShadow castShadow renderOrder={2} frustumCulled={false}>
        <meshStandardMaterial
          color={color}
          roughness={YAK_TRAILS_CLIENT_TUNING.trail.surfaceRoughness}
          side={DoubleSide}
          polygonOffset
          polygonOffsetFactor={YAK_TRAILS_CLIENT_TUNING.trail.polygonOffsetFactor}
        />
      </mesh>
      {liveEndpoint && points.length > 0 && (
        <LiveTrailTip from={points[points.length - 1]!} endpoint={liveEndpoint} color={color} contactColor={contactColor} />
      )}
    </group>
  );
}
